import React from 'react';
import { Download, Share, X } from 'lucide-react';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Dismissible "Install app" banner (OMN-370). Renders only when the app is
 * installable and not already installed. On Chromium it triggers the native
 * prompt; on iOS Safari it shows the manual Add-to-Home-Screen instructions.
 */
export const PwaInstallBanner: React.FC = () => {
  const { visible, isIOS, install, dismiss } = usePwaInstall();
  const { t } = useLanguage();

  if (!visible) return null;

  return (
    <div className="bg-blue-50 border-b border-blue-100" role="region" aria-label={t('pwa.install.title', { defaultValue: 'Install OmniFlyer' })}>
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2 text-sm">
        <Download className="hidden h-5 w-5 shrink-0 text-blue-600 sm:block" aria-hidden="true" />
        <div className="flex-1 text-blue-900">
          <span className="font-medium">{t('pwa.install.title', { defaultValue: 'Install OmniFlyer' })}</span>{' '}
          {isIOS ? (
            <span className="text-blue-800">
              {t('pwa.install.iosHint', {
                defaultValue: 'Tap the Share icon, then “Add to Home Screen”.',
              })}
            </span>
          ) : (
            <span className="text-blue-800">
              {t('pwa.install.subtitle', { defaultValue: 'Add it to your home screen for quick, full-screen access.' })}
            </span>
          )}
        </div>

        {isIOS ? (
          <Share className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
        ) : (
          <button
            type="button"
            onClick={install}
            className="shrink-0 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            {t('pwa.install.action', { defaultValue: 'Install' })}
          </button>
        )}

        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
          className="shrink-0 rounded-md p-1 text-blue-500 hover:bg-blue-100 hover:text-blue-700"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
